import { NavLink } from "react-router";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { UI_VERSION } from "@/lib/buildInfo";
import { usePreferences } from "@/lib/storage";
import { cn } from "@/lib/utils";
import { useFlags } from "../flags";
import { SECTION_LABEL, visibleNav, type NavItem } from "./nav";
import { useShellData } from "./shellData";

export function NavItems({ collapsed, onNavigate }: { collapsed: boolean; onNavigate?: () => void }) {
  const { user, pendingCount } = useShellData();
  const enabled = useFlags();
  const items = visibleNav(user?.role, enabled);
  const sections: Array<NavItem["section"]> = ["testing", "insights", "admin"];
  const renderItem = (n: NavItem) => {
    const link = (
      <NavLink
        to={n.to}
        end={n.to === "/"}
        onClick={onNavigate}
        className={({ isActive }) =>
          cn(
            "relative flex h-9 items-center gap-3 rounded-md px-3 text-sm text-sidebar-foreground hover:bg-muted",
            isActive &&
              "bg-muted font-medium before:absolute before:inset-y-1 before:left-0 before:w-0.5 before:rounded-full before:bg-signal",
            n.primary && !collapsed && "bg-primary text-primary-foreground hover:bg-primary/90",
            collapsed && "justify-center px-0",
          )
        }
      >
        <n.icon aria-hidden className="size-4 shrink-0" />
        {!collapsed && <span className="truncate">{n.label}</span>}
        {collapsed && <span className="sr-only">{n.label}</span>}
        {n.badge === "pending" && pendingCount > 0 && !collapsed && (
          <span className="ml-auto rounded-full bg-tone-amber-bg px-1.5 text-xs text-tone-amber-fg" aria-label={`${pendingCount} waiting`}>
            {pendingCount}
          </span>
        )}
      </NavLink>
    );
    return (
      <li key={n.id}>
        {collapsed ? (
          <Tooltip>
            <TooltipTrigger asChild>{link}</TooltipTrigger>
            <TooltipContent side="right">{n.label}</TooltipContent>
          </Tooltip>
        ) : (
          link
        )}
      </li>
    );
  };
  return (
    <div className="flex h-full flex-col gap-4">
      {sections.map((s) => {
        const list = items.filter((i) => i.section === s);
        if (!list.length) return null;
        return (
          <div key={s}>
            {!collapsed && <div className="px-3 pb-1 text-xs font-medium text-muted-foreground">{SECTION_LABEL[s]}</div>}
            <ul className="flex flex-col gap-0.5">{list.map(renderItem)}</ul>
          </div>
        );
      })}
      <div className="mt-auto">
        <ul className="flex flex-col gap-0.5">{items.filter((i) => i.section === "bottom").map(renderItem)}</ul>
      </div>
    </div>
  );
}

export function Sidebar() {
  const [prefs] = usePreferences();
  const collapsed = prefs.sidebarCollapsed;
  return (
    <aside
      className={cn(
        "sticky top-14 hidden h-[calc(100dvh-56px)] shrink-0 flex-col border-r bg-sidebar p-2 lg:flex no-print",
        collapsed ? "w-16" : "w-62",
      )}
    >
      <nav aria-label="Main" className="min-h-0 flex-1 overflow-auto">
        <NavItems collapsed={collapsed} />
      </nav>
      {!collapsed && <div className="border-t px-2 pt-2 text-xs text-muted-foreground">UI v{UI_VERSION}</div>}
    </aside>
  );
}
